import {
    MedewerkersOpStation,
    useGetMedewerkersOpStations,
    usePatchMedewerkersOpStation,
    usePostMedewerkersOpStationAanvraag,
} from "@/entities/medewerkers-op-station";
import { useSortByStationDisplayName } from "@/entities/station";
import { NieuwContactpersoon } from "@/features/contactpersonen";
import { Button, Card, ConfirmDialog, ErrorDisplay, Icon, Separated } from "@/shared/components";
import { useMemo, useState } from "react";
import { InzetVoorwaarden, Medewerkers } from "../shared";
import { AddMedewerkersStationDialog } from "./add-medewerkers-station";
import { MedewerkersStation } from "./medewerkers-station";

type MedewerkersOpStationDetailsProps = {
    dossierNummer: string;
    aanvraag: MedewerkersOpStation[];
};

export function MedewerkersOpStationDetailsRegievoerder({ dossierNummer, aanvraag }: MedewerkersOpStationDetailsProps) {
    const [manualStationCodes, setManualStationCodes] = useState<string[]>([]);
    const { data: manualStations, error: manualStationsError } = useGetMedewerkersOpStations(manualStationCodes);
    const requestVossenMutation = usePostMedewerkersOpStationAanvraag();
    const patchMedewerkersOpStation = usePatchMedewerkersOpStation();

    const [internalStationCodesSelected, setInternalStationCodesSelected] = useState<Set<string>>(() => new Set());
    const [addManualStationDialogOpen, setAddManualStationDialogOpen] = useState(false);
    const [stopVosInzetDialogOpen, setStopVosInzetDialogOpen] = useState(false);
    const [addMolDialogOpen, setAddMolDialogOpen] = useState(false);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    // after requesting vossen / adding mollen for a manual station, the aanvraag is refetched. Now, the manual station is part of the aanvraag.
    // To avoid a duplicate stations flicker in the UI (for the short duration where the aanvraag has fresh data but the manualStations data is not updated yet) we deduplicate here.
    const stations = useMemo(
        () => [
            ...aanvraag,
            ...(manualStations.filter((manualStation) => !aanvraag.find((station) => station.stationCode === manualStation.stationCode)) ??
                []),
        ],
        [aanvraag, manualStations]
    );
    const sortedStations = useSortByStationDisplayName(stations);

    if (manualStationsError) {
        return <ErrorDisplay error={manualStationsError} title="Fout bij het laden van handmatig toegevoegde stations" />;
    }

    const handleStationSelectionChange = (stationCode: string, isSelected: boolean) => {
        setInternalStationCodesSelected((prev) => {
            const next = new Set(prev);
            if (isSelected) {
                next.add(stationCode);
                setErrorMessage(null);
            } else {
                next.delete(stationCode);
            }
            return next;
        });
    };

    const handleAddManualStation = (code: string) => {
        setManualStationCodes((prev) =>
            prev.includes(code) || aanvraag.find((station) => station.stationCode === code) ? prev : [...prev, code]
        );

        // Optimistically select; actual selectability determined after data fetch completes (because only then we know if there are vossen available and the station is safe)
        handleStationSelectionChange(code, true);
        setAddManualStationDialogOpen(false);
    };

    const handleRequestVossenSuccess = (stationCodes: string[]) => {
        setInternalStationCodesSelected(new Set());
        // When requesting vossen for a manual station, the station gets added to the aanvraag that the BE returns, so we have to remove it from local state.
        setManualStationCodes((prev) => prev.filter((code) => !stationCodes.includes(code)));
    };

    const handleRequestVossen = (stationCodes: string[]) => {
        requestVossenMutation.mutate(
            { dossierId: dossierNummer, stationCodes },
            {
                onSuccess: () => handleRequestVossenSuccess(stationCodes),
            }
        );
    };

    const handleCreateMol = (stationCode: string) => {
        // actual creation happens inside the contactpersoon dialog.
        // When creating a MOL for a manual station, the station gets added to the aanvraag that the BE returns, so we have to remove it from local state.
        setManualStationCodes((prev) => prev.filter((code) => code !== stationCode));
    };

    const handleStopInzet = () => {
        const acceptedVossenCount = sortedStations.reduce(
            (acc, station) => acc + station.vossen.filter((vos) => vos.status === "Geaccepteerd").length,
            0
        );
        if (acceptedVossenCount === 0) setStopVosInzetDialogOpen(false);

        patchMedewerkersOpStation.mutate(
            {
                dossierNummer,
                actie: "Stop",
                scope: "Alles",
            },
            {
                onSuccess: () => setStopVosInzetDialogOpen(false),
            }
        );
    };

    // Filter out auto-selected stations that don't meet selectability criteria
    // (added manually before fetching their veilig/count status)
    const selectableStationCodes = new Set(
        sortedStations.filter((s) => s.status === "NietAangevraagd" && s.veilig && s.aantalBeschikbareVossen > 0).map((s) => s.stationCode)
    );
    const stationCodesSelected = internalStationCodesSelected.intersection(selectableStationCodes);
    const selectedStationsCount = stationCodesSelected.size;

    return (
        <>
            <InzetVoorwaarden className="mb-8" />
            <div className="mb-4 flex items-center justify-between font-medium">
                <h2>Betrokken stations</h2>
                <MedewerkersOpStationDetailsActions
                    medewerkersOpStations={aanvraag}
                    onStopVosInzet={() => setStopVosInzetDialogOpen(true)}
                    onAddManualStation={() => setAddManualStationDialogOpen(true)}
                    onAddMol={() => setAddMolDialogOpen(true)}
                />
            </div>
            {requestVossenMutation.error && (
                <div className="py-3">
                    <ErrorDisplay error={requestVossenMutation.error} />
                </div>
            )}
            <Card className="mb-8 rounded-xl py-0">
                <ul>
                    <Separated>
                        {sortedStations.map((station) => {
                            const hasMedewerkers = [...station.vossen, ...station.mollen].length > 0;
                            return (
                                <li key={station.stationCode} data-testid={`vos-station-li-${station.stationCode}`}>
                                    <MedewerkersStation
                                        station={station}
                                        isSelected={stationCodesSelected.has(station.stationCode)}
                                        onSelectionChange={handleStationSelectionChange}
                                        onRequestVosSuccess={(stationCode: string) => {
                                            handleRequestVossenSuccess([stationCode]);
                                        }}
                                        onCreateMol={handleCreateMol}
                                    />
                                    {hasMedewerkers && <Medewerkers vossen={station.vossen} mollen={station.mollen} />}
                                </li>
                            );
                        })}
                    </Separated>
                </ul>
            </Card>

            {errorMessage ? (
                <p className={"text-destructive text-s mb-1 flex items-center gap-2"}>
                    <Icon name="error-filled" size="xs" />
                    {errorMessage}
                </p>
            ) : (
                <p className="text-content-subtle mb-1">
                    {selectedStationsCount} station{selectedStationsCount === 1 ? "" : "s"} geselecteerd
                </p>
            )}
            <Button
                onClick={() => {
                    if (selectedStationsCount === 0) {
                        setErrorMessage("Selecteer een of meer stations om VOS'en aan te vragen");
                        return;
                    }
                    handleRequestVossen([...stationCodesSelected.values()]);
                }}
                disabled={requestVossenMutation.isPending}
            >
                Verstuur VOS aanvraag
                {requestVossenMutation.isPending && <Icon name="loader" size="xs" className="animate-spin" />}
            </Button>
            {addManualStationDialogOpen && (
                <AddMedewerkersStationDialog
                    open={addManualStationDialogOpen}
                    onOpenChange={setAddManualStationDialogOpen}
                    onSubmit={handleAddManualStation}
                />
            )}
            {addMolDialogOpen && (
                <NieuwContactpersoon dossierId={dossierNummer} functie="MOL" open={addMolDialogOpen} onOpenChange={setAddMolDialogOpen} />
            )}
            {stopVosInzetDialogOpen && (
                <ConfirmDialog
                    isOpen={stopVosInzetDialogOpen}
                    onCancel={() => setStopVosInzetDialogOpen(false)}
                    onConfirm={handleStopInzet}
                    isPending={patchMedewerkersOpStation.isPending}
                    error={patchMedewerkersOpStation.error}
                    title="VOS inzet stoppen"
                    confirmLabel="Inzet stopzetten"
                    cancelLabel="Annuleren"
                    destructive
                >
                    Weet je zeker dat je alle VOS inzet wilt stopzetten? Alle ingezette VOS'en krijgen een sms bericht dat inzet niet langer
                    nodig is.
                </ConfirmDialog>
            )}
        </>
    );
}

type MedewerkersOpStationDetailsActionsProps = {
    medewerkersOpStations: MedewerkersOpStation[];
    onStopVosInzet: () => void;
    onAddManualStation: () => void;
    onAddMol: () => void;
};

function MedewerkersOpStationDetailsActions({
    medewerkersOpStations,
    onStopVosInzet,
    onAddManualStation,
    onAddMol,
}: MedewerkersOpStationDetailsActionsProps) {
    const showStopVos = medewerkersOpStations.some((station) => station.status === "AanvraagLoopt" || station.status === "VosIngezet");
    return (
        <div className="flex gap-4">
            {showStopVos && (
                <Button
                    variant="secondary"
                    size="s"
                    destructive
                    icon="users"
                    iconStrokeWidth="thick"
                    iconSize="xs"
                    onClick={onStopVosInzet}
                >
                    VOS inzet stopzetten
                </Button>
            )}
            <Button variant="secondary" size="s" icon="station" iconStrokeWidth="thick" iconSize="xs" onClick={onAddManualStation}>
                Station toevoegen
            </Button>
            <Button variant="secondary" size="s" icon="user" iconStrokeWidth="thick" iconSize="xs" onClick={onAddMol}>
                MOL toevoegen
            </Button>
        </div>
    );
}
